import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { immediateToast } from "izitoast-react";
import { StoreDispatch } from "src/page/store";
import {
  AttributeCatalog,
  AttributeCatalogValueType,
  CreateAttributeCatalogRequest,
  GetAttributeCatalogRequest,
  UpdateAttributeCatalogRequest,
  createAttributeCatalog,
  getAttributeCatalog,
  updateAttributeCatalog
} from "src/network/graphql/transactionService";
import { AttributeCatalogType, Status } from "src/types/commonTypes";
import { activeStatusMapping } from "src/components/constant/constant";

export type MasterCatalogRow = {
  attributeCatalogId: string;
  displayId: string;
  code: string;
  type: AttributeCatalogType;
  title: string;
  status: Status;
  organisationId?: string;
  createdAt?: string;
  updatedAt?: string;
  version?: string;
  appliesTo?: string[];
  valueType?: AttributeCatalogValueType;
  options?: string[];
};

const mapRow = (item: AttributeCatalog): MasterCatalogRow => ({
  attributeCatalogId: item.attribute_catalog_id,
  displayId: item.attribute_catalog_id,
  code: item.code,
  type: item.type,
  title: item.title,
  status: item.status,
  organisationId: item.organisation_id,
  createdAt: item.created_at,
  updatedAt: item.updated_at,
  version: item.version,
  appliesTo: item.attributes?.applies_to ?? undefined,
  valueType: item.attributes?.value_type ?? undefined,
  options: item.attributes?.options ?? undefined
});

const initialList: MasterCatalogRow[] = [];

const typeLabel = (type: AttributeCatalogType): string => {
  if (type === "GL_CODE") return "GL Code";
  if (type === "ACCOUNTING_CATEGORY") return "Accounting Category";
  if (type === "CUSTOM_ATTRIBUTE") return "Custom Attribute";
  return type;
};

const masterCatalogSlice = createSlice({
  name: "masterCatalog",
  initialState: {
    listLoading: false,
    saveLoading: false,
    statusChangeLoading: false,
    error: null as unknown,
    entries: {
      list: initialList,
      total: 0
    }
  },
  reducers: {
    fetchListStart: (state) => {
      state.listLoading = true;
    },
    fetchListSuccess: (state, action: PayloadAction<{ list: MasterCatalogRow[]; total: number }>) => {
      state.listLoading = false;
      state.entries.list = action.payload.list;
      state.entries.total = action.payload.total;
    },
    fetchListFail: (state, action: PayloadAction<unknown>) => {
      state.listLoading = false;
      state.entries.list = [];
      state.entries.total = 0;
      state.error = action.payload;
    },
    saveStart: (state) => {
      state.saveLoading = true;
    },
    saveDone: (state) => {
      state.saveLoading = false;
    },
    saveFail: (state, action: PayloadAction<unknown>) => {
      state.saveLoading = false;
      state.error = action.payload;
    },
    statusChangeStart: (state) => {
      state.statusChangeLoading = true;
    },
    statusChangeDone: (state) => {
      state.statusChangeLoading = false;
    },
    statusChangeFail: (state, action: PayloadAction<unknown>) => {
      state.statusChangeLoading = false;
      state.error = action.payload;
    },
    updateRowStatus: (state, action: PayloadAction<{ attributeCatalogId: string; status: Status }>) => {
      const target = state.entries.list.find((row) => row.attributeCatalogId === action.payload.attributeCatalogId);

      if (target) target.status = action.payload.status;
    }
  }
});

const {
  fetchListStart,
  fetchListSuccess,
  fetchListFail,
  saveStart,
  saveDone,
  saveFail,
  statusChangeStart,
  statusChangeDone,
  statusChangeFail,
  updateRowStatus
} = masterCatalogSlice.actions;

export type FetchMasterCatalogParams = {
  type: AttributeCatalogType;
  from: number;
  size: number;
  code?: string;
  title?: string;
  status?: Status;
};

export const fetchMasterCatalogAction = (params: FetchMasterCatalogParams) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchListStart());
    try {
      const request: GetAttributeCatalogRequest = {
        from: params.from,
        size: params.size,
        attribute_catalog_filter: {
          type: params.type,
          code: params.code,
          title: params.title,
          status: params.status
        }
      };
      const response = await getAttributeCatalog(request);
      const items: AttributeCatalog[] = response.data.search_attribute_catalog?.items ?? [];
      const total: number = response.data.search_attribute_catalog?.total_size ?? 0;

      dispatch(
        fetchListSuccess({
          list: items.map(mapRow),
          total
        })
      );
    } catch (error) {
      dispatch(fetchListFail(error));
      immediateToast("error", {
        message: "Failed to load master catalog",
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

export const createMasterCatalogEntryAction = (
  data: CreateAttributeCatalogRequest,
  cb?: () => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(saveStart());
    try {
      const response = await createAttributeCatalog(data);
      const created: AttributeCatalog | undefined = response.data?.create_attribute_catalog;

      dispatch(saveDone());
      if (!created) return;

      const successLabel = `${typeLabel(data.type)} added`;

      immediateToast("success", {
        message: successLabel,
        timeout: 3000,
        position: "topCenter"
      });
      cb?.();
    } catch (error) {
      dispatch(saveFail(error));
      throw error;
    }
  };
};

export const updateMasterCatalogEntryAction = (
  data: UpdateAttributeCatalogRequest,
  cb?: () => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(saveStart());
    try {
      const response = await updateAttributeCatalog(data);
      const updated: AttributeCatalog | undefined = response.data?.update_attribute_catalog;

      dispatch(saveDone());
      if (!updated) return;

      const successLabel = `${typeLabel(data.type)} updated`;

      immediateToast("success", {
        message: successLabel,
        timeout: 3000,
        position: "topCenter"
      });
      cb?.();
    } catch (error) {
      dispatch(saveFail(error));
      throw error;
    }
  };
};

export const changeMasterCatalogStatusAction = (
  data: UpdateAttributeCatalogRequest,
  cb?: () => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(statusChangeStart());
    try {
      const response = await updateAttributeCatalog(data);
      const updated: AttributeCatalog | undefined = response.data?.update_attribute_catalog;

      dispatch(statusChangeDone());
      if (updated) {
        dispatch(
          updateRowStatus({
            attributeCatalogId: updated.attribute_catalog_id,
            status: updated.status
          })
        );
      }

      const isDeactivate = !activeStatusMapping[data.status];

      immediateToast("success", {
        message: `${typeLabel(data.type)} ${isDeactivate ? "deactivated" : "reactivated"}`,
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb();
    } catch (error) {
      dispatch(statusChangeFail(error));
      throw error;
    }
  };
};

export default masterCatalogSlice.reducer;
